import { inject, Injectable, NgZone } from '@angular/core';
import { AppDB } from './dexieDB';
import { Table } from 'src/app/models/ui/table';
import { DBStatus, MRproResponse, MRproResponseInfo, Media, ID } from 'src/app/types/api';
import {
  WartungsBaumNode,
  WartungsPlanNode,
} from 'src/app/content/_components/_overlays/wartungsverwaltung/wartung.types';
import {
  MultiProtokollModel,
  ProtokollIDs,
  ProtokollModel,
  WartungProtokollAPI,
} from 'src/app/content/Wartung/wartungdialog/wartung-protokoll.types';
import {
  FileAPIModel,
  FileForPreview,
} from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnail.type';
import { AsMappedTableFE, GetDBStatus, kbezInRange, SharedDB } from './sharedDB';
import { HilfeBeschreibung } from 'src/app/content/_components/_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { EinstellungenDB } from './einstellungenDB';
import { distinct } from 'src/app/misc/functions';

//#region Interfaces for DB Structure
export interface KWAART extends DBStatus, HilfeBeschreibung {
  ID?: number;
  Bezeichnung: string;
  Typ: 0 | 1 | 2;
  OTYPID: number;
}
export interface KWAARB extends DBStatus {
  ID?: number;
  Bezeichnung: string;
  OTYPID: number;
}
export interface KZUWA extends DBStatus {
  ID?: number;
  RevArtID: number;
  RevArbID: number;
  Priorität: number;
  OTYPID: number;
}
export interface KWAPRS extends DBStatus {
  ID?: number;
  Bezeichnung: string;
  OTYPID: number;
}
export interface KWAMAN extends DBStatus {
  ID?: number;
  Name: string;
  OTYPID: number;
}
export interface KWAMAS extends DBStatus {
  ID?: number;
  RevMaskenNameID: number;
  RevArtID: number;
  Sortierung: number;
  OTYPID: number;
}
export interface KWASTM extends DBStatus {
  ID?: number;
  OSTAMMID: number;
  RevMaskenID: number;
  RevIntervall: number;
  Zustaedigkeit: number;
  RevBeginn: string;
  IntervallTyp: number;
  IntervallArt?: number;
}
export interface KWAPRU extends DBStatus {
  ID?: number;
  RevStammID: number;
  RevMaskenNameID: number;
  RevBemerkung: string | null;
  RevDatum: string;
  OTYPID: number;
  RevBild: string;
}
export interface KWAMGZ extends DBStatus {
  ID?: number;
  RevPruefungID: number;
  RevMaskenNameID: number;
  RevArtID: number;
  RevArbID?: number;
  RevGeprueft: boolean;
  RevZustand_iO: boolean;
  RevPriorität: number;
  RevUmfang?: number;
  RevME?: string;
  RevBild?: string;
  Arbeiter: string;
  Messwert?: number;
  OMAZSID?: number;
}
export interface KWAMZN {
  ID?: number;
  RevMaskenNameID: number;
  SZAEHNID: number;
}
//#endregion

@Injectable({ providedIn: 'root' })
export class WartungDB {
  private readonly zone = inject(NgZone);

  private readonly db = inject(AppDB);

  private readonly einstellungenDB = inject(EinstellungenDB);
  private readonly sharedDB = inject(SharedDB);

  //#region Wartungsbaum
  async SetWartungsBaumNode(OTYPID: number, req: WartungsBaumNode): Promise<MRproResponseInfo<ID>> {
    try {
      const item: KZUWA = {
        RevArtID: req.artID,
        RevArbID: req.arbeitID,
        Priorität: req.Priorität,
        OTYPID,
        ...GetDBStatus(),
      };
      return { success: true, ID: await this.db.kzuwa.add(item) };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }
  //#endregion

  async GetWartungenBemerkungen(): Promise<string[]> {
    try {
      const rows = await this.db.kwapru.toArray();
      return distinct(rows, 'RevBemerkung');
    } catch (_) {
      return [];
    }
  }

  async GetWartungenInPlan(type: string): Promise<WartungsPlanNode[] | Table<WartungsPlanNode>> {
    return await this.zone.runOutsideAngular(async () => {
      try {
        const groupIDs = await this.sharedDB.GetGroupIDs();
        const zuordnungen = await this.db.kwastm
          .filter(x => !!x.OSTAMMID && !!x.RevBeginn)
          .reverse()
          .sortBy('RevBeginn');

        const planNodes = await Promise.all(
          zuordnungen.map(async ({ OSTAMMID, RevMaskenID: mID, ...zuordnung }) => {
            const objekt = await this.db.ostamm.where({ OSTAMMID }).first();
            if (
              !objekt ||
              !(await this.db.sxmgr
                .where('BenutzerGruppeID')
                .anyOf(groupIDs)
                .and(x => x.AnlagenobjektID == objekt.OSTAMMID)
                .first())
            )
              return null;

            const sotyp = await this.db.sotyp.get(objekt.OTYPID);
            if (!sotyp) return null;

            const maske = await this.db.kwaman.get(mID);
            const personal = await this.db.kwaprs.get(zuordnung.Zustaedigkeit);
            const wartungen = await this.db.kwapru.where({ RevStammID: OSTAMMID, RevMaskenNameID: mID }).toArray();
            const wartung = findLatestWartung(wartungen);
            const pruefung =
              !wartung ? null : (
                await this.db.opruef
                  .where({ OSTAMMID })
                  .and(x => x.P_Datum == wartung.RevDatum && x.P_IntStatus == 'Wartung')
                  .first()
              );

            const planNode: WartungsPlanNode = {
              OSTAMMID,
              OTYPID: objekt.OTYPID,
              strBez: [sotyp.Bezeichnung, objekt.Bezirk, objekt.Stellwerk, objekt.Bezeichnung]
                .filter(x => !!x)
                .join(' - '),
              diffFromNow: null,
              nextDatum: null,
              ID: zuordnung.ID,
              OPRUEFID: pruefung?.OPRUEFID,
              wID: wartung?.ID,
              letzteDatum: wartung?.RevDatum,
              bemerkung: wartung?.RevBemerkung,
              RevBild: wartung?.RevBild,
              archiv_mID: mID,
              mID: maske?.ID,
              maske: maske?.Name,
              pID: personal?.ID,
              verantwortlich: personal?.Bezeichnung,
              intervall: zuordnung.RevIntervall,
              einheit: zuordnung.IntervallTyp == 0 ? 'Tag' : 'Monat',
              intervallbeginn: zuordnung.RevBeginn,
              icon: sotyp.SysIcon,
            };

            return { ...planNode, ...objekt };
          })
        );

        if (type == 'table') {
          const kbez = await this.sharedDB.GetKundenBezeichnungColumnNames();

          return AsMappedTableFE<WartungsPlanNode>()(planNodes, [
            'OSTAMMID',
            'OTYPID',
            'strBez',
            'diffFromNow',
            'nextDatum',
            'Bezeichnung',
            ['MessRegBezirk', 'MessReg Bezirk'],
            ...kbezInRange([, 2], kbez),
            'ID',
            'OPRUEFID',
            'wID',
            'letzteDatum',
            'bemerkung',
            'RevBild',
            'archiv_mID',
            'mID',
            'maske',
            'pID',
            'verantwortlich',
            'intervall',
            'einheit',
            'intervallbeginn',
            ...kbezInRange([2, 9], kbez),
            'Startpunkt',
            'Endpunkt',
            'StartMeter',
            'EndMeter',
            'icon',
          ]);
        }

        return planNodes;
      } catch (_) {
        return null;
      }
    });
  }

  async GetWartungFromMaske(mID: number | null, wID: number, isReadOnly: boolean): Promise<WartungProtokollAPI[]> {
    return await this.zone.runOutsideAngular(async () => {
      try {
        const isNew: boolean = wID == -1;
        let wartungsItems: WartungProtokollAPI[];
        if (isNew && mID) {
          const maskenItems = await this.db.kwamas.where({ RevMaskenNameID: mID }).sortBy('Sortierung');

          wartungsItems = await Promise.all(
            maskenItems.map(async (maskeItem: KWAMAS) => {
              const art: KWAART = await this.db.kwaart.get(maskeItem.RevArtID);
              if (!art?.ID) return null;
              return {
                rSort: maskeItem.Sortierung,
                ...(await this.getWartungProtokollModel(art, 'withZuordnungen')),
              };
            })
          );
        } else {
          const protokollItems = await this.db.kwamgz.where({ RevPruefungID: wID }).sortBy('ID');

          wartungsItems = await Promise.all(
            protokollItems.map(async protokollItem => {
              const { RevArtID: artID, RevArbID: arbeitID } = protokollItem;
              const art = await this.db.kwaart.get(artID);
              if (!art) return null;
              const arb = arbeitID ? await this.db.kwaarb.get(arbeitID) : null;

              return {
                ID: protokollItem.ID,
                OMAZSID: protokollItem.OMAZSID || null,
                ...(await this.getWartungProtokollModel(
                  art,
                  isReadOnly ? 'skipZuordnungen' : 'withZuordnungen',
                  protokollItem,
                  arb?.Bezeichnung
                )),
              };
            })
          );
        }
        return wartungsItems.filter(row => row !== null);
      } catch (_) {
        return null;
      }
    });
  }

  async SetWartung(protokoll: ProtokollModel): Promise<MRproResponseInfo<ProtokollIDs>> {
    try {
      let { wID, opruefid } = protokoll;
      const { ostammid, otypid, mID, datum, bemerkung, form, maske, personal } = protokoll;

      await this.db.transaction(
        'rw',
        [this.db.kwapru, this.db.opruef, this.db.omazs, this.db.omzlog, this.db.kwamgz, this.db.sysini, this.db.media],
        async () => {
          const DBStatus = GetDBStatus();

          if (!wID) {
            const mediaStr = await this.sharedDB.SaveMedia(
              'Passbilder/Wartungsbilder',
              protokoll.files,
              null,
              `${protokoll.zID}`
            );
            wID = await this.db.kwapru.add({
              RevStammID: ostammid,
              RevMaskenNameID: mID,
              RevDatum: datum,
              RevBemerkung: bemerkung,
              RevBild: mediaStr,
              OTYPID: otypid,
              ...DBStatus,
            });
          } else
            await this.db.kwapru.update(wID, {
              RevDatum: datum,
              RevBemerkung: bemerkung,
              ...DBStatus,
            });

          if (!opruefid) opruefid = await this.createPruefungForWartung(ostammid, datum, otypid, DBStatus);
          else await this.db.opruef.where({ OPRUEFID: opruefid }).modify({ P_Datum: datum, ...DBStatus });

          if (!form) return;

          let lfNr = 0;
          const { optDownChangeFK: gefahrFK, optMINNumFK: minFK } = await this.einstellungenDB.GetOptions(
            'optDownChangeFK',
            'optMINNumFK'
          );

          for (const [key, headerBlock] of Object.entries(form)) {
            for (const [artKey, werte] of Object.entries(headerBlock)) {
              const artID = +artKey.split('-').pop();
              if (isNaN(artID)) continue;

              if (!werte.iO && werte.arbeitID > 0) {
                lfNr++;
                const headerName = key == '0' ? maske : key.slice(key.indexOf('-') + 1);

                werte.omazsid ||= await this.createMangelFromWartung(ostammid, opruefid, headerName, otypid, DBStatus);
                await this.db.omazs.where({ OMAZSID: werte.omazsid }).modify({
                  lfNr,
                  Mangel: werte.wartungsart,
                  Instandsetzung: werte.massnahmen,
                  FK: werte.fk ?? -1,
                  Betriebsgefahr: gefahrFK && minFK == werte.fk,
                  Umfang: werte.umfang,
                  Mengeneinheit: werte.meh,
                  ...DBStatus,
                });
              } else if (werte.iO && werte.omazsid) {
                await this.db.omazs.where({ OMAZSID: werte.omazsid }).delete();
                werte.omazsid = null;
              }

              const protokollItem = {
                RevArbID: werte.arbeitID ?? 0,
                Messwert: werte.messwert,
                RevPriorität: werte.fk ?? -1,
                RevUmfang: werte.umfang,
                RevME: werte.meh,
                RevZustand_iO: werte.iO,
                OMAZSID: werte.omazsid ?? 0,
                ...DBStatus,
              };

              if (!werte.ID) {
                const mediaStr = await this.sharedDB.SaveMedia(
                  'Passbilder/Wartungsbilder',
                  werte.files,
                  null,
                  `${wID}_${artID}`
                );
                werte.ID = await this.db.kwamgz.add({
                  RevPruefungID: wID,
                  RevMaskenNameID: mID,
                  RevGeprueft: true,
                  RevArtID: artID,
                  Arbeiter: personal,
                  RevBild: mediaStr,
                  ...protokollItem,
                });
              } else await this.db.kwamgz.update(werte.ID, protokollItem);
            }
          }
        }
      );

      return { success: true, wID, pruefID: opruefid };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  async SetMultiWartung(protokoll: MultiProtokollModel): Promise<MRproResponse> {
    try {
      await this.db.transaction('rw', [this.db.kwapru, this.db.kwamas], async () => {
        for (const objekt of protokoll.objekte) {
          const wID = await this.db.kwapru.add({
            RevStammID: objekt.OSTAMMID,
            RevMaskenNameID: objekt.mID,
            RevDatum: protokoll.datum,
            RevBemerkung: protokoll.bemerkung,
            OTYPID: +objekt.otypid,
            RevBild: null,
            ...GetDBStatus(),
          });

          const arten = await this.db.kwamas.where({ RevMaskenNameID: objekt.mID }).sortBy('Sortierung');
          for (const { RevArtID } of arten) {
            await this.db.kwamgz.add({
              RevPruefungID: wID,
              RevMaskenNameID: objekt.mID,
              RevArtID: RevArtID,
              RevGeprueft: true,
              RevZustand_iO: true,
              RevPriorität: -1,
              Arbeiter: '',
              ...GetDBStatus(),
            });
          }
        }
      });

      return { success: true };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  async GetArbeitID(Bezeichnung: string, OTYPID: number): Promise<number> {
    try {
      const arb = await this.db.kwaarb
        .where({ OTYPID })
        .and(x => x.Bezeichnung == Bezeichnung)
        .first();
      return arb?.ID ?? (await this.db.kwaarb.add({ Bezeichnung, OTYPID, ...GetDBStatus() }));
    } catch (_) {
      return null;
    }
  }

  async GetWartungMediaByID(wID: number): Promise<FileForPreview[]> {
    const names = (await this.db.kwapru.get(wID)).RevBild;
    return (await this.GetWartungMediaAllByNames(names?.split('|')))?.files;
  }

  async SetWartungsImage(
    isPassbild: boolean,
    wID: number,
    id: number,
    sendObj: FileAPIModel
  ): Promise<MRproResponseInfo<{ mediaStr: string }>> {
    try {
      const { files, savedFiles, deletedFiles } = sendObj;

      const mediaStr = await this.db.transaction(
        'rw',
        [this.db.media, isPassbild ? this.db.kwapru : this.db.kwamgz],
        async () => {
          const addToName = `${wID}${isPassbild ? '' : `_${id}`}`;
          const mediaStr = await this.sharedDB.SaveMedia('Passbilder/Wartungsbilder', files, savedFiles, addToName);
          await this.sharedDB.CleanMedia(deletedFiles);

          if (isPassbild) await this.db.kwapru.update(wID, { RevBild: mediaStr });
          else await this.db.kwamgz.update(id, { RevBild: mediaStr });
          return mediaStr;
        }
      );

      return { success: true, mediaStr };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  async GetWartungMediaAllByNames(names: string[]): Promise<MRproResponseInfo<Media>> {
    try {
      const files = await this.sharedDB.GetMediaByNames(names);
      return { success: true, files };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  async GetWartungMediaByName(filename: string): Promise<string> {
    try {
      return (await this.sharedDB.GetMediaByName(filename))?.base64;
    } catch (_) {
      return null;
    }
  }

  private async getArtArbeitFK_Zuordnungen(RevArtID: number) {
    const zuordList = await this.db.kzuwa.where({ RevArtID }).toArray();
    const zuordnungen = (
      await Promise.all(
        zuordList.map(async zuordnung => {
          const arb: KWAARB = await this.db.kwaarb.get(zuordnung.RevArbID);
          if (!arb) return null;
          return `${zuordnung.RevArbID}|.|${arb?.Bezeichnung}|.|${zuordnung?.Priorität}`;
        })
      )
    )
      .filter(x => !!x)
      .join('&.&');
    return zuordnungen;
  }
  private async getWartungProtokollModel(
    art: KWAART,
    mode: 'withZuordnungen' | 'skipZuordnungen',
    protokollItem?: KWAMGZ,
    arbeitName?: string
  ): Promise<WartungProtokollAPI> {
    return await this.zone.runOutsideAngular(async () => {
      const {
        RevArbID: arbeitID,
        RevZustand_iO: iO,
        Messwert,
        RevME,
        RevBild,
        RevPriorität: fk,
        RevUmfang: umfang,
      } = protokollItem ?? {};
      const zuordnungen = mode == 'withZuordnungen' ? await this.getArtArbeitFK_Zuordnungen(art.ID) : null;

      return {
        artID: art.ID,
        artName: art.Bezeichnung,
        artTyp: art.Typ as 0 | 1 | 2,
        FileTyp: art.FileTyp,
        HelpBeschreibung: art.HelpBeschreibung,
        zuordnungen,
        arbeitName,
        iO,
        Messwert,
        arbeitID,
        RevME,
        RevBild,
        FK: fk > -1 ? fk : null,
        RevUmfang: umfang > 0 ? umfang : null,
      };
    });
  }
  private async createPruefungForWartung(OSTAMMID: number, P_Datum: string, otypid: number, DBStatus: DBStatus) {
    return await this.db.transaction('rw?', this.db.opruef, async () => {
      const id = await this.db.opruef.add({
        OSTAMMID,
        P_DelStatus: 0,
        P_IntStatus: 'Wartung',
        P_Datum,
        KPRTYPID: 1,
        ...DBStatus,
      });

      const OPRUEFID = +otypid.toString().padEnd(7, '0') + id;

      await this.db.opruef.update(id, { OPRUEFID });
      return OPRUEFID;
    });
  }
  private async createMangelFromWartung(
    OSTAMMID: number,
    OPRUEFID: number,
    Pruefart: string,
    OTYPID: number,
    DBStatus: DBStatus
  ) {
    return await this.db.transaction('rw?', [this.db.omazs, this.db.omzlog], async () => {
      const id = await this.db.omazs.add({
        OSTAMMID,
        OPRUEFID,
        Pruefart,
        OMARTID: 5,
        nIO: true,
        Auftrag: false,
        Erledigt: false,
        ...DBStatus,
      });

      const OMAZSID = +OTYPID.toString().padEnd(9, '0') + id;

      await this.db.omazs.update(id, { OMAZSID });
      await this.db.omzlog.add({ OMAZSID, LogTyp: 'Neu', AuftragNr: 0, ...DBStatus });
      return OMAZSID;
    });
  }
}

function findLatestWartung(wartungen: KWAPRU[]) {
  return wartungen.reduce(
    (latest, wartung) => {
      latest.wartung ??= wartung;
      const diff = wartung.RevDatum.localeCompare(latest.date) as -1 | 0 | 1;
      if (diff === 1 || (diff === 0 && wartung.ID > latest.id))
        return { date: wartung.RevDatum, id: wartung.ID, wartung };

      return latest;
    },
    { date: '', id: 0, wartung: null as KWAPRU }
  ).wartung;
}
